import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import { setActiveModule, selectActiveModuleId } from "../store/slices/moduleSlice";
import { MODULES, MODULE_IDS } from "../config/modules";

/**
 * Hook to automatically sync the active module based on the current route
 * This ensures that when users navigate directly to a URL, the correct module is selected
 */
export const useModuleSync = () => {
  const location = useLocation();
  const dispatch = useAppDispatch();
  const activeModuleId = useAppSelector(selectActiveModuleId);
  const isInitialMount = useRef(true);

  useEffect(() => {
    // Skip on initial mount to avoid conflicts
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    const pathname = location.pathname;

    // Skip if we're on dashboard or root admin path - dashboard is module-aware
    // so we don't want to change the module when on dashboard
    if (pathname === "/admin" || pathname === "/admin/" || pathname === "/admin/dashboard") {
      return;
    }

    // Detect module from route path
    let detectedModuleId = null;

    // Special case: /admin/media routes belong to Events & Media module
    if (pathname.startsWith("/admin/media")) {
      detectedModuleId = MODULE_IDS.EVENTS_MEDIA;
    }
    // Special case: /admin/insurance routes belong to Insurance module
    else if (pathname.startsWith("/admin/insurance")) {
      detectedModuleId = MODULE_IDS.INSURANCE;
    }
    // Special case: /admin/travel routes belong to Travel & Tours module
    else if (pathname.startsWith("/admin/travel")) {
      detectedModuleId = MODULE_IDS.TRAVEL_TOURS;
    }
    // Special case: /admin/safety routes belong to Safety Services module
    else if (pathname.startsWith("/admin/safety")) {
      detectedModuleId = MODULE_IDS.SAFETY_SERVICES;
    }
    // Check if path matches any module's base path
    else {
      for (const module of MODULES) {
        if (pathname.startsWith(module.path)) {
          detectedModuleId = module.id;
          break;
        }
      }
    }

    if (detectedModuleId && detectedModuleId !== activeModuleId) {
      dispatch(setActiveModule(detectedModuleId));
    }
  }, [location.pathname, dispatch, activeModuleId]);
};

